import {MATH_TYPE, PER_USER_AGGREGATIONS, PropertyType, ValueRepresentationType} from 'common/report/constants';
import {ComparisonDateRangeBookmarkFormat} from 'common/widgets/query-block/time-comparison-menu/types';
import {ChartType} from 'common/report/constants';
import {BaseDate, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {CustomProperty} from 'common/report/stores/custom-properties';
import type {HeaderType} from 'common/util/header-sorting';

export interface FlatSegment {
  key: string;
  path: string[];
  value: Record<string, number>; // object key is an ISO-8601 date string
  displayName: string;
  rootUnwrapped: boolean;
}

export type AbsoluteDateRange = {
  fromDate: string;
  toDate: string;
};

export enum SortBy {
  Value = `value`,
  Column = `column`,
  Label = `label`,
}

export enum SortOrder {
  Ascending = `asc`,
  Descending = `desc`,
}

export enum ChartPlotStyle {
  Standard = `standard`,
  Stacked = `stacked`,
}

export enum AnalysisType {
  Linear = `linear`,
  Rolling = `rolling`,
  Logarithmic = `logarithmic`,
  Cumulative = `cumulative`,
}

export interface DisplayOptions {
  chartType: InsightsChartType;
  plotStyle: ChartPlotStyle;
  analysis: AnalysisType;
  value: ValueRepresentationType;
  rollingWindowSize?: number;
  timeUnit?: TimeUnit;
}

export type InsightsChartType = ChartType.Bar | ChartType.Line | ChartType.Pie | ChartType.Table;
export interface FlatSortingOptions {
  sortBy: SortBy.Value | SortBy.Label;
  sortOrder: SortOrder;
}

export interface SeriesSortingOptions {
  colSortAttrs: FlatSortingOptions[];
  sortBy: SortBy.Column | SortBy.Value;
  sortOrder?: SortOrder;
}

export interface SortConfig {
  [ChartType.Bar]: SeriesSortingOptions;
  [ChartType.Table]: SeriesSortingOptions;
  [ChartType.Line]: FlatSortingOptions;
}

export enum InsightsResourceType {
  Events = `events`,
  People = `people`,
  Cohorts = `cohorts`,

  // @deprecated the "all" resource type is no longer valid for Insights (for more info, search for "test_patch_for_resource_type_all_bug" in query-api code)
  All = `all`,
}

export interface ShowClause {
  math: MATH_TYPE;
  perUserAggregation?: PER_USER_AGGREGATIONS;
  resourceType: InsightsResourceType;
  profileType?: string;
  value: {
    name: string | string[];
    renamed?: string;
    custom?: boolean;
    id?: number;
  };
  property?: {
    type: PropertyType;
    name: string | string[];
    resourceType: InsightsResourceType;
    unit?: TimeUnit;
  };
  filter?: TODO;
  formulaLabel?: string;
  labelSuffix?: string;
  labelPrefix?: string;
  dataGroupId?: string;
  dataset?: string;
  datasetId?: string;
  projectId?: number;
  customBucket?: TODO;
}

export interface GroupClause {
  propertyType?: PropertyType;
  resourceType: InsightsResourceType;
  typeCast?: PropertyType;
  unit?: TimeUnit;
  dataset?: string;
  datasetId?: string;
  projectId?: number;
  dataGroupId?: string;
  tempDataGroupId?: string;
  joinPropertyType?: PropertyType;
  customPropertyId?: number | string;
  customProperty?: CustomProperty;
  value: string;
  propertyObjectKey?: string | string[];
}

export interface FormulaClause {
  value: string;
  label?: string;
}

export interface TimeClause {
  unit: TimeUnit;
  value?: number | string | [BaseDate] | [BaseDate, BaseDate];
  from_date?: BaseDate;
  to_date?: BaseDate;
  rolling?: boolean;
  window?: {
    unit: TimeUnit;
    value: number;
  };
  dateRangeType?: DateRangeType;
  dataset?: string;
  time_aggregation_unit?: TimeUnit;
  as_of_timestamp?: number;
  time_comparison?: TODO;
}

export interface InsightsBookmarkSections {
  show: ShowClause[];
  cohorts?: TODO[];
  group?: GroupClause[];
  filter?: TODO[];
  formula?: FormulaClause[];
  time: TimeClause[];
}

export interface InsightsBookmarkParams {
  columnWidths: TODO;
  displayOptions: DisplayOptions;
  name: string;
  sections: InsightsBookmarkSections;
  sorting: Partial<SortConfig>;
  timeComparison?: ComparisonDateRangeBookmarkFormat;
  selectedSegments?: string[];
}

export type InsightsSeriesTree = {[k: string]: number} | {[k: string]: InsightsSeriesTree};

interface DateRange {
  // sic; this is NOT BookmarkDateRange or a similar bookmark-related type
  from_date: string;
  to_date: string;
}

export interface MetricMetadata {
  metric_key?: string;
  sub_queries?: Array<{metric_key: string}>;
  /*
    Augmented on the client-side by queries/insights based on query limits and segments in the result
  */
  apiResultsTruncated?: boolean;
}

export interface ReportSectionsMetadata {
  group?: Array<{bookmark: GroupClause}>;
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
}

export interface SegmentationQueryLimits {
  limit?: number;
}

export interface InsightsQueryResults {
  computed_at: string;
  date_range: DateRange;
  headers: string[];
  meta: {
    min_sampling_factor: number;
    report_sections: ReportSectionsMetadata;
    /*
    Augmented on the client-side to carry the limits used on the query that generated these results.
    When limits affect the results, we need to tell the user where the cutoff is.
    */
    queryLimits?: SegmentationQueryLimits;
  };
  series: InsightsSeriesTree;
  time_comparison?: {
    date_range: DateRange;
    series: InsightsSeriesTree;
  };
}

interface SumTreeSegment {
  label: string;
  value: number;
  timeComparisonValue: number;
  headerType: HeaderType;
  path: string[];
}

/**
 * SumTree where children at each depth are a nested object. Necessary for quick segment lookups by path
 * such as accessing time comparison segments or tooltip stats.
 */
export interface UnorderedSumTree {
  [label: string]: UnorderedSumTreeNode;
}

export interface UnorderedSumTreeNode extends SumTreeSegment {
  children?: UnorderedSumTree;
}

/**
 * SumTree where children at each depth are an array. Necessary for manipulating and displaying ordered data
 * where there could be children with duplicate segments e.g. when sorting by value.
 */
export interface OrderedSumTree extends SumTreeSegment {
  children?: OrderedSumTree[];
}
export interface AggregatedSeriesValue {
  // list of all possible paths to reach the segment in this series
  paths: string[][];

  // sum of all of this segments values in this series
  total: number;
}

export interface AggregatedSeries {
  [label: string]: AggregatedSeriesValue;
}

export interface InsightsLegendUrlData {
  idxSelections?: number[] | number[][];
  otherSelections?: boolean[];
}
